import { useState, type ChangeEvent, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useMutation } from '@tanstack/react-query'
import { KeyRound } from 'lucide-react'
import { AdminUsernameSchema, isStrongAdminPassword } from '@novelaide/shared-types/admin-auth'
import { AdminApiError } from '@/api/errors'
import { adminsApi } from '@/api/services/admins'
import { Button } from '@/components/ui/button'
import { Field } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { toast } from '@/lib/toast'

const ERROR_KEY_MAP: Record<string, string> = {
  unauthorized: 'auth.bootstrap.error.unauthorized',
  admin_bootstrap_already_initialized: 'auth.bootstrap.error.alreadyInitialized',
  invalid_username: 'auth.bootstrap.error.invalidUsername',
  admin_password_too_weak: 'auth.bootstrap.error.weakPassword',
  network_error: 'auth.bootstrap.error.network'
}

export default function BootstrapRootToken() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [rootToken, setRootToken] = useState('')
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [displayName, setDisplayName] = useState('')
  const bootstrapMutation = useMutation({
    mutationFn: () =>
      adminsApi.bootstrap({
        rootToken: rootToken.trim(),
        username,
        password,
        displayName: displayName.trim() || null
      }),
    onSuccess: () => {
      void navigate('/login', { replace: true })
    },
    onError: (err) => {
      if (err instanceof AdminApiError) {
        toast.error(t(ERROR_KEY_MAP[err.errorCode] ?? 'auth.bootstrap.error.unknown'))
      } else {
        toast.error(t('auth.bootstrap.error.unknown'))
      }
    }
  })
  const submitting = bootstrapMutation.isPending
  const usernameValid = AdminUsernameSchema.safeParse(username).success
  const passwordValid = isStrongAdminPassword(password)
  const canSubmit = rootToken.trim().length > 0 && usernameValid && passwordValid && !submitting

  function handleTokenChange(e: ChangeEvent<HTMLInputElement>) {
    setRootToken(e.target.value)
  }

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    if (submitting) return
    if (!rootToken.trim()) {
      toast.error(t('auth.bootstrap.error.empty'))
      return
    }
    if (!usernameValid) {
      toast.error(t('auth.bootstrap.error.invalidUsername'))
      return
    }
    if (!passwordValid) {
      toast.error(t('auth.bootstrap.error.weakPassword'))
      return
    }

    bootstrapMutation.mutate()
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-6">
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-sm space-y-4 rounded-lg border border-border bg-card p-6 shadow-sm"
      >
        <div className="flex items-start gap-3">
          <div className="rounded-md border border-border bg-secondary/50 p-2">
            <KeyRound className="h-4 w-4" />
          </div>
          <div className="space-y-1">
            <h1 className="text-lg font-semibold">{t('auth.bootstrap.title')}</h1>
            <p className="text-xs text-muted-foreground">{t('auth.bootstrap.subtitle')}</p>
          </div>
        </div>

        <Field label={t('auth.bootstrap.token')} labelFor="root-token">
          <Input
            id="root-token"
            name="root-token"
            type="password"
            autoComplete="off"
            autoFocus
            required
            value={rootToken}
            onChange={handleTokenChange}
            className="h-auto w-full px-3 py-2 shadow-none"
          />
        </Field>

        <Field
          label={t('auth.bootstrap.username')}
          labelFor="admin-username"
          hint={t('auth.bootstrap.usernameHelp')}
        >
          <Input
            id="admin-username"
            name="admin-username"
            type="text"
            autoComplete="username"
            required
            minLength={3}
            maxLength={32}
            pattern="[a-z0-9]+"
            value={username}
            onChange={(event) => setUsername(event.target.value)}
            className="h-auto w-full px-3 py-2 shadow-none"
          />
        </Field>

        <Field
          label={t('auth.bootstrap.password')}
          labelFor="admin-password"
          hint={t('auth.bootstrap.passwordHelp')}
        >
          <Input
            id="admin-password"
            name="admin-password"
            type="password"
            autoComplete="new-password"
            required
            minLength={8}
            maxLength={256}
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            className="h-auto w-full px-3 py-2 shadow-none"
          />
        </Field>

        <Field
          label={t('auth.bootstrap.displayName')}
          labelFor="display-name"
          hint={t('auth.bootstrap.hint')}
        >
          <Input
            id="display-name"
            name="display-name"
            type="text"
            maxLength={80}
            value={displayName}
            onChange={(event) => setDisplayName(event.target.value)}
            className="h-auto w-full px-3 py-2 shadow-none"
          />
        </Field>

        <Button type="submit" disabled={!canSubmit} loading={submitting} className="w-full">
          {t('auth.bootstrap.submit')}
        </Button>
      </form>
    </div>
  )
}
